<template>
    <div class="alert alert-warning" v-if="!create && !addresses.length">
        <i class="fa fa-exclamation-triangle mr-2"/>
        {{ $t('The address book is empty.') }}
    </div>
    <ValidationObserver ref="validation" v-else>
        <label :for="getTagId('AddressBook')">{{ $t('Address book') }}</label>
        <fieldset :id="getTagId('AddressBook')">
            <div class="form-row">
                <div class="form-group col-lg-6">
                    <label class="sr-only" :for="getTagId('Address')">{{ $t('Address') }}</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text"><i class="fa fa-book"></i></div>
                        </div>
                        <select :id="getTagId('Address')" v-model="addressId" class="form-control" @change="updateAddress">
                            <option v-if="create" :value="null">{{ $t('Use a new address…') }}</option>
                            <option v-for="address in addresses" :value="address.id">
                                {{ renderAddressOption(address) }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </fieldset>
        <div v-show="addressId !== undefined">
            <label :for="getTagId('ContractData')">{{ $t('Contact data') }}</label>
            <fieldset :id="getTagId('ContractData')">
                <div class="form-row">
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('FirstName')">{{ $t('First name') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="firstName">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-user"></i></div>
                                </div>
                                <input :id="getTagId('FirstName')" v-model="address.firstName" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('First name')" class="form-control"
                                       required type="text" @change="updateFirstName"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('LastName')">{{ $t('Last name') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="lastName">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-user"></i></div>
                                </div>
                                <input :id="getTagId('LastName')" v-model="address.lastName" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Last name')"
                                       class="form-control" required type="text" @change="updateLastName"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('Company')">{{ $t('Company') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="company">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-landmark"></i></div>
                                </div>
                                <input :id="getTagId('Company')" v-model="address.company" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Company')"
                                       class="form-control" type="text" @change="updateCompany"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('Tax')">{{ $t('Tax Id') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="tax">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-hashtag"></i></div>
                                </div>
                                <input :id="getTagId('Tax')" v-model="address.tax" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Tax Id')"
                                       class="form-control" required type="text" @change="updateTax"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('Email')">{{ $t('Email') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" rules="email" vid="email">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-envelope"></i></div>
                                </div>
                                <input :id="getTagId('Email')" v-model="address.email" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Email')"
                                       class="form-control" required type="text" @change="updateEmail"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('Phone')">{{ $t('Phone') }}</label>
                        <IntlTelInput :id="getTagId('Phone')" v-model="address.phone" :disabled="!!addressId" :label="$t('Phone')" required
                                      @change="updatePhone"/>
                    </div>
                </div>
            </fieldset>
            <label :for="getTagId('AddressData')">{{ $t('Address data') }}</label>
            <fieldset :id="getTagId('AddressData')">
                <div class="form-row">
                    <div class="form-group col-md-12">
                        <label class="sr-only" :for="getTagId('Line1')">{{ $t('Street address') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="line1">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-road"></i></div>
                                </div>
                                <input :id="getTagId('Line1')" v-model="address.line1" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Street address')"
                                       class="form-control" required type="text" @change="updateLine1"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-12">
                        <label class="sr-only" :for="getTagId('Line2')">{{ $t('Building, floor, door, etc') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="line2">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-door-closed"></i></div>
                                </div>
                                <input :id="getTagId('Line2')" v-model="address.line2" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId"
                                       :placeholder="$t('Building, floor, door, etc')" class="form-control" type="text"
                                       @change="updateLine2"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-8">
                        <label class="sr-only" :for="getTagId('City')">{{ $t('City') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="city">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-city"></i></div>
                                </div>
                                <input :id="getTagId('City')" v-model="address.city" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('City')"
                                       class="form-control" required type="text" @change="updateCity"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-4">
                        <label class="sr-only" :for="getTagId('Zip')">{{ $t('Zip code') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="zip">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-hashtag"></i></div>
                                </div>
                                <input :id="getTagId('Zip')" v-model="address.zip" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Zip code')"
                                       class="form-control" required type="text" @change="updateZip"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('Country')">{{ $t('Country') }}</label>
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-globe-americas"></i></div>
                            </div>
                            <select2 :id="getTagId('Country')" v-model="countryId" :placeholder="$t('Country')"
                                     :options="countries" class="form-control" :disabled="!!addressId" @change="updateCountry">
                            </select2>
                        </div>
                    </div>
                    <div v-show="states.length" class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('State')">{{ $t('State') }}</label>
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-border-all"></i></div>
                            </div>
                            <select2 :id="getTagId('State')" v-model="stateId" :placeholder="$t('State')"
                                     :options="states" class="form-control" :disabled="!!addressId" @change="updateState">
                            </select2>
                        </div>
                    </div>
                    <div class="form-group col-md-12">
                        <label class="sr-only" :for="getTagId('Notes')">{{ $t('Notes') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" rules="" vid="notes">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-sticky-note"></i></div>
                                </div>
                                <input :id="getTagId('Notes')" v-model="address.notes" v-bind="ariaInput"
                                       :class="classes" :disabled="addressId" :placeholder="$t('Notes')"
                                       class="form-control" type="text" @change="updateNotes"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                </div>
            </fieldset>
        </div>
        <div v-show="addressId === null">
            <label :for="getTagId('SaveData')">{{ $t('Save address')}}</label>
            <fieldset :id="getTagId('SaveData')">
                <div class="form-row">
                    <div class="form-group col-12">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text">
                                    <input :id="getTagId('Save')" v-model="save" :aria-label="$t('Save to address book')" type="checkbox" @change="updateSave">
                                </div>
                            </div>
                            <div class="form-control">
                                <label class="form-check-label" :for="getTagId('Save')">{{ $t('Save to address book') }}</label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-row">
                    <div v-show="save" class="form-group col-md-6">
                        <label class="sr-only" :for="getTagId('Name')">{{ $t('Name') }}</label>
                        <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" vid="name">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <div class="input-group-text"><i class="fa fa-save"></i></div>
                                </div>
                                <input :id="getTagId('Name')" v-model="address.name" v-bind="ariaInput" :class="classes" :placeholder="$t('Name')"
                                       class="form-control" required type="text" @change="updateName"/>
                                <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                </div>
            </fieldset>
        </div>
    </ValidationObserver>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import Select2 from "../Select2";
import IntlTelInput from "../Input/IntlTelInput";

export default {
    name: "AddressForm",
    components: {IntlTelInput, Select2, ValidationProvider, ValidationObserver },
    props: {
        id: {type: String},
        create: {type: Boolean, default: true},
    },
    data() {
        let state = this.$store.state.order[this.id];
        let newAddress = {
            id: null,
            firstName: undefined,
            lastName: undefined,
            company: undefined,
            tax: undefined,
            email: undefined,
            phone: undefined,
            line1: undefined,
            line2: undefined,
            city: undefined,
            zip: undefined,
            state: undefined,
            country: undefined,
            notes: undefined,
        };
        return {
            newAddress: newAddress,
            address: state.address ?? newAddress,
            addressId: state.address.id,
            countryId: state.address.country?.id,
            stateId: state.address.state?.id,
            save: state.save,
        }
    },
    computed: {
        errors() {
            return this.$refs.validation ? this.$refs.validation.$data.errors : undefined;
        },
        addresses() {
            return this.$store.state.order[this.id].addresses;
        },
        countries() {
            return this.$store.state.order[this.id].countries;
        },
        states() {
            return this.address.country ? this.address.country.states : [];
        },
        country() {
            return this.countries.find(country => country.id === this.countryId);
        },
        state() {
            return this.states.find(state => state.id === this.stateId);
        },
    },
    watch: {
        countries(countries) {
            this.newAddress.country = countries.find(country => country.code === 'ES');
        }
    },
    methods: {
        getTagId(name) {
            return this.id + name;
        },
        renderAddressOption(address) {
            if (address.name) {
                return address.name;
            }
            return address.firstname + ' ' + address.lastname + ' @ ' + address.line1 + ', ' + address.city;
        },
        updateAddress() {
            this.address = this.addresses.find(address => address.id === this.addressId) ?? this.newAddress;
            this.countryId = this.address.country?.id;
            // Wait for states to be computed and passed to the select2 before sending the value
            setTimeout(() => this.stateId = this.address.state?.id, 100);
            this.$store.commit('order/' + this.id + '/setAddress', this.address);
        },
        updateFirstName() {
            this.$store.commit('order/' + this.id + '/setFirstName', this.errors.firstName.length ? undefined : this.address.firstName);
        },
        updateLastName() {
            this.$store.commit('order/' + this.id + '/setLastName', this.errors.lastName.length ? undefined : this.address.lastName);
        },
        updateCompany() {
            this.$store.commit('order/' + this.id + '/setCompany', this.errors.company.length ? undefined : this.address.company);
        },
        updateTax() {
            this.$store.commit('order/' + this.id + '/setTax', this.errors.tax.length ? undefined : this.address.tax);
        },
        updateEmail() {
            this.$store.commit('order/' + this.id + '/setEmail', this.errors.email.length ? undefined : this.address.email);
        },
        updatePhone() {
            this.$store.commit('order/' + this.id + '/setPhone', this.address.phone);
        },
        updateLine1() {
            this.$store.commit('order/' + this.id + '/setLine1', this.errors.line1.length ? undefined : this.address.line1);
        },
        updateLine2() {
            this.$store.commit('order/' + this.id + '/setLine2', this.errors.line2.length ? undefined : this.address.line2);
        },
        updateCity() {
            this.$store.commit('order/' + this.id + '/setCity', this.errors.city.length ? undefined : this.address.city);
        },
        updateZip() {
            this.$store.commit('order/' + this.id + '/setZip', this.errors.zip.length ? undefined : this.address.zip);
        },
        updateState() {
            if (!this.addressId) {
                this.$store.commit('order/' + this.id + '/setState', this.state);
            }
        },
        updateCountry() {
            if (!this.addressId) {
                this.$store.commit('order/' + this.id + '/setCountry', this.country);
                this.stateId = undefined;
                this.updateState();
            }
        },
        updateNotes() {
            this.$store.commit('order/' + this.id + '/setNotes', this.errors.notes.length ? undefined : this.address.notes);
        },
        updateSave() {
            this.$store.commit('order/' + this.id + '/setSave', this.save);
        },
        updateName() {
            this.$store.commit('order/' + this.id + '/setName', this.errors.name.length ? undefined : this.address.name);
        },
    },
    mounted() {
        this.$store.dispatch('order/' + this.id + '/loadAddresses');
        this.$store.dispatch('order/' + this.id + '/loadCountries');
    },
}
</script>
